"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var tslib_1 = require("tslib");
var React = require("react");
var TypeStyle = require("typestyle");
var SharedWidgets_1 = require("../../SharedWidgets");
var TabStyles_1 = require("../../styles/TabStyles");
var BrowserDetection_1 = require("../../utils/BrowserDetection");
var DomUtil_1 = require("../../utils/DomUtil");
var FindIndex_1 = require("../../utils/FindIndex");
var ReactHelpers_1 = require("../../utils/ReactHelpers");
var List_1 = require("./List");
var ListItemUtils_1 = require("./ListItemUtils");
var ListStyles_1 = require("./ListStyles");
var FieldWrapperStyleName = TypeStyle.style({
    flexShrink: 0,
    padding: TabStyles_1.TabStyles.Spacing.SmallSpace,
});
var FieldStyle = {
    width: '100%',
};
function findSelectedItem(selectedValue, items) {
    // not using `findSelectableItemIndex` because it doesn't wrap and skips the current value
    var selectedItemIndex = FindIndex_1.findIndex(items, function (item) { return ListItemUtils_1.isSelectableItem(item) && item.value === selectedValue; });
    return items[selectedItemIndex];
}
/**
 * Use `FilteringList` when it's necessary to have custom filtering logic, e.g. via a server request or for custom formatting
 * of the items based on filtering results. Otherwise most cases will probably just want `LocallyFilteringList`.
 */
var FilteringList = /** @class */ (function (_super) {
    tslib_1.__extends(FilteringList, _super);
    function FilteringList() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.listId = DomUtil_1.generateId();
        _this.state = {};
        _this.handleFocusCapture = ReactHelpers_1.mergeEventHandlers(function (e) {
            if (_this.inputElement && e.target !== _this.inputElement) {
                _this.inputElement.focus();
            }
        }, function (e) { return ReactHelpers_1.safeInvoke(_this.props.onFocusCapture, e); });
        _this.clearInput = function () {
            _this.handleInputChange({ target: { value: '' } });
            _this.setState({ selectedItem: undefined });
        };
        _this.handleInputChange = function (e) {
            if (_this.props.onFilterChange) {
                _this.props.onFilterChange(e.target.value);
            }
        };
        _this.keyDownHandlers = {
            'Ctrl+Home': function () { return _this.moveSelection(-1, +1); },
            'Ctrl+End': function () { return _this.moveSelection(_this.props.items.length, -1); },
            'ArrowUp': function () { return _this.moveSelection(_this.props.items.indexOf(_this.state.selectedItem), -1); },
            'ArrowDown': function () { return _this.moveSelection(_this.props.items.indexOf(_this.state.selectedItem), +1); },
            'Enter': function () {
                if (_this.props.onSelect && _this.state.selectedItem) {
                    _this.props.onSelect(_this.state.selectedItem.value);
                }
            },
            'Escape': function () {
                if (_this.state.selectedItem) {
                    _this.setState({ selectedItem: undefined });
                    return false;
                }
                return true;
            }
        };
        _this.handleKeyDown = ReactHelpers_1.mergeEventHandlers(function (e) {
            var key = (BrowserDetection_1.isCtrl(e) ? 'Ctrl+' : '') + e.key;
            if (_this.keyDownHandlers[key]) {
                if (!_this.keyDownHandlers[key](e)) { // handler returning falsy means the event was handled
                    e.stopPropagation();
                    e.preventDefault();
                }
            }
        }, function (e) { return ReactHelpers_1.safeInvoke(_this.props.onKeyDown, e); });
        _this.handleDivRef = function (ref) {
            _this.rootElement = ref;
            if (!ref) {
                _this.setState({ width: undefined });
            }
            else {
                // pin the width to prevent the widget from shrinking when items are filtered
                window.setTimeout(function () {
                    if (document.body.contains(ref)) {
                        _this.setState({ width: Math.ceil(ref.getBoundingClientRect().width) }, _this.fixHeightForIE);
                    }
                });
            }
        };
        _this.fixHeightForIE = function () {
            if (!BrowserDetection_1.isIE) {
                return;
            }
            _this.rootElement.style.height = '';
            var height = _this.rootElement.getBoundingClientRect().height;
            _this.rootElement.style.height = Math.ceil(height) + 'px';
        };
        _this.handleSelect = function (value) {
            _this.setState({ selectedItem: findSelectedItem(value, _this.props.items) });
            if (_this.props.onSelect) {
                _this.props.onSelect(value);
            }
        };
        _this.updateInputRef = function (ref) {
            _this.inputElement = ref;
        };
        return _this;
    }
    FilteringList.prototype.componentDidUpdate = function (prevProps, prevState) {
        var _a = this.props, items = _a.items, preserveSelection = _a.preserveSelection;
        var selectedItem = this.state.selectedItem;
        var prevSelectedItem = prevState.selectedItem || {};
        // preserve selection across item list updates
        if (prevProps.items !== items) {
            if (preserveSelection && selectedItem && prevSelectedItem.value === selectedItem.value) {
                this.setState({ selectedItem: findSelectedItem(selectedItem.value, items) });
            }
            else {
                this.setState({ selectedItem: undefined });
            }
        }
        if (prevProps.items !== items || prevProps.children !== this.props.children) {
            this.fixHeightForIE();
        }
        // scroll the selected item into view
        if (selectedItem && ((prevSelectedItem.value !== selectedItem.value) || (prevProps.items !== items))) {
            var elem = document.getElementById(this.listId + "-" + items.indexOf(selectedItem));
            if (elem) {
                DomUtil_1.scrollIntoView(elem);
            }
        }
    };
    FilteringList.prototype.componentDidMount = function () {
        if (this.props.autoFocus) {
            DomUtil_1.timeoutFocus(this.inputElement);
        }
    };
    FilteringList.prototype.moveSelection = function (start, direction) {
        var items = this.props.items;
        this.setState({ selectedItem: items[ListItemUtils_1.findSelectableItemIndex(items, start, direction)] });
    };
    FilteringList.prototype.render = function () {
        // tslint:disable-next-line:max-line-length
        var _a = this.props, autoFocus = _a.autoFocus, children = _a.children, className = _a.className, listTestId = _a.listTestId, inputTestId = _a.inputTestId, items = _a.items, onFilterChange = _a.onFilterChange, onSelect = _a.onSelect, preserveSelection = _a.preserveSelection, reverse = _a.reverse, searchPlaceholder = _a.searchPlaceholder, searchText = _a.searchText, style = _a.style, testId = _a.testId, divProps = tslib_1.__rest(_a, ["autoFocus", "children", "className", "listTestId", "inputTestId", "items", "onFilterChange", "onSelect", "preserveSelection", "reverse", "searchPlaceholder", "searchText", "style", "testId"]);
        var selectedValue = this.state.selectedItem && this.state.selectedItem.value;
        // tslint:disable-next-line:no-any SingleSelectDropdown overrides this prop when it clones regardless of List type
        delete divProps['selectOnEnterKey'];
        return (React.createElement("div", tslib_1.__assign({}, divProps, { className: TypeStyle.classes(ListStyles_1.FilteringListStyleName, reverse && 'reverse', className), "data-tb-test-id": testId, onFocusCapture: this.handleFocusCapture, onKeyDown: this.handleKeyDown, ref: this.handleDivRef, style: tslib_1.__assign({ width: this.state.width }, style) }),
            React.createElement(SharedWidgets_1.TextFieldWidget, { "aria-activedescendant": selectedValue ? this.listId + "-" + items.indexOf(this.state.selectedItem) : undefined, "aria-autocomplete": 'list', "aria-controls": this.listId, "aria-expanded": true, "aria-label": divProps['aria-label'], "aria-labelledby": divProps['aria-labelledby'], className: FieldWrapperStyleName, kind: 'search', label: undefined, onChange: this.handleInputChange, onClear: this.clearInput, placeholder: searchPlaceholder, ref: this.updateInputRef, role: 'combobox', style: FieldStyle, testId: inputTestId, value: searchText }),
            children,
            React.createElement(List_1.List, { disableTabAccessibility: true, id: this.listId, items: items, onSelect: this.handleSelect, selectedValue: selectedValue, selectOnEnterKey: false, testId: listTestId })));
    };
    FilteringList.defaultProps = {
        preserveSelection: true,
    };
    return FilteringList;
}(React.Component));
exports.FilteringList = FilteringList;
//# sourceMappingURL=FilteringList.js.map